/**
 * The authenticated shell: `Sidebar` plus a main column, replacing the flat `<header>` nav.
 *
 * Split from `App.tsx` so the shell — a layout decision — is testable and reviewable apart from the
 * routing and role-gating decisions that still live there. `App.tsx` keeps deciding *whether* Content
 * and Support are reachable; this component only decides how the rail and the content column are
 * arranged once that decision is made.
 */
import type { StaffRole } from '@apuestapr/contracts';
import { cn, Toaster } from '@apuestapr/ui';
import { useState, type ReactElement, type ReactNode } from 'react';

import { Sidebar } from './Sidebar.tsx';

export interface AppShellProps {
  readonly showContent: boolean;
  readonly showSupport: boolean;
  /** See the identical note on `SidebarProps` — kept named the same across the two for consistency. */
  readonly showLocations: boolean;
  /** Staff management is admin only, so this is `true` for exactly one role. */
  readonly showStaff: boolean;
  /**
   * Named `staffRole`, not `role`: a bare `role` prop on a JSX element is checked by
   * `jsx-a11y/aria-role` as if it were the DOM ARIA attribute, and `StaffRole` values like
   * `"admin"` are not valid ARIA roles. The lint rule cannot tell a component prop from a DOM
   * attribute by name alone, so the fix is to not share the name.
   */
  readonly staffRole: StaffRole;
  readonly userName: string;
  readonly footer: ReactElement;
  readonly children: ReactNode;
}

export const AppShell = ({
  showContent,
  showSupport,
  showLocations,
  showStaff,
  staffRole,
  userName,
  footer,
  children,
}: AppShellProps): ReactElement => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div
      className={cn(
        'grid min-h-screen',
        collapsed ? 'grid-cols-shell-collapsed' : 'grid-cols-shell',
      )}
    >
      <Sidebar
        showContent={showContent}
        showSupport={showSupport}
        showLocations={showLocations}
        showStaff={showStaff}
        staffRole={staffRole}
        userName={userName}
        footer={footer}
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((value) => !value)}
      />
      <main className="min-w-0 px-6 py-8">{children}</main>
      <Toaster />
    </div>
  );
};
